/*
 * Copyright (c) 2020.  Rikesh Lal Shrestha
 * All Rights Reserved
 */

import axios from 'axios';

import {
  ADMIN_ACCESS_CODE,
  ADMIN_JWT_TOKEN,
  ADMIN_SELECTED_LANGUAGE,
  API_URL,
  CLIENT_ACCESS_CODE,
  CLIENT_JWT_TOKEN,
  CLIENT_SELECTED_LANGUAGE,
  SYSTEM_JWT_TOKEN,
} from '../constants/appConfig';
import {
  systemLogoutLocalStorage,
  clientLogoutLocalStorage,
  adminLogoutLocalStorage,
} from './authUtil';
import history from './history';
import { getCurrentTimeZone } from './commonUtil';
import { getLocalStorage, setLocalStorage } from './storageUtil';

export const httpSystemBase = (isUpload = false) => {
  const header = {
    'X-XSRF-TOKEN': getLocalStorage(SYSTEM_JWT_TOKEN),
    Pragma: 'no-cache',
    'X-TIME-ZONE': getCurrentTimeZone(),
    // 'client-device': 'web', // Might need later
  };
  const normalHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  const uploadableHeaders = {
    'Content-Type': 'multipart/form-data',
  };

  const api = axios.create({
    baseURL: `${API_URL}`,
    headers: isUpload ? { ...header, ...uploadableHeaders } : { ...header, ...normalHeaders },
    responseType: 'json',
  });

  api.interceptors.response.use(
    (response) => {
      if (response.headers && response.headers['x-xsrf-token']) {
        setLocalStorage(SYSTEM_JWT_TOKEN, response.headers['x-xsrf-token']);
      }
      return response;
    },
    (error) => {
      if (401 === error.response.status) {
        systemLogoutLocalStorage();
        history.push(`/system/login`);
      }
      if (404 === error.response.status) {
        history.push('/system/404');
      }
      return Promise.reject(error);
    }
  );

  return api;
};

export const httpAdminBase = (isUpload = false) => {
  const header = {
    'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
    Pragma: 'no-cache',
    'X-TIME-ZONE': getCurrentTimeZone(),
    'client-device': 'web',
    lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
  };
  const normalHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  const uploadableHeaders = {
    'Content-Type': 'multipart/form-data',
  };

  const api = axios.create({
    baseURL: `${API_URL}`,
    headers: isUpload ? { ...header, ...uploadableHeaders } : { ...header, ...normalHeaders },
    responseType: 'json',
  });

  api.interceptors.response.use(
    (response) => {
      if (response.headers && response.headers['x-xsrf-token']) {
        setLocalStorage(ADMIN_JWT_TOKEN, response.headers['x-xsrf-token']);
      }
      return response;
    },
    (error) => {
      if (401 === error.response.status) {
        localStorage.setItem('COMPANY_UNAUTHORIZED', true);
        clearAdminLocalStorageAndRedirectToLogin();
      }
      if (404 === error.response.status) {
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );

  return api;
};

export const httpClientBase = (isUpload = false) => {
  const header = {
    'X-XSRF-TOKEN': getLocalStorage(CLIENT_JWT_TOKEN),
    Pragma: 'no-cache',
    'X-TIME-ZONE': getCurrentTimeZone(),
    lang: getLocalStorage(CLIENT_SELECTED_LANGUAGE),
    // 'client-device': 'web', // Might need later
  };
  const normalHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  const uploadableHeaders = {
    'Content-Type': 'multipart/form-data',
  };

  const api = axios.create({
    baseURL: `${API_URL}`,
    headers: isUpload ? { ...header, ...uploadableHeaders } : { ...header, ...normalHeaders },
    responseType: 'json',
  });

  api.interceptors.response.use(
    (response) => {
      if (response.headers && response.headers['x-xsrf-token']) {
        setLocalStorage(CLIENT_JWT_TOKEN, response.headers['x-xsrf-token']);
      }
      return response;
    },

    (error) => {
      if (401 === error.response.status) {
        clientLogoutLocalStorage();
        history.push(`/${getLocalStorage(CLIENT_ACCESS_CODE)}?&unauthorized=true`);
      }

      if (404 === error.response.status) {
        history.push('/client/404');
      }
      return Promise.reject(error);
    }
  );

  return api;
};

export const clearAdminLocalStorageAndRedirectToLogin = () => {
  return (
    adminLogoutLocalStorage() && history.push(`/${getLocalStorage(ADMIN_ACCESS_CODE)}/admin/login`)
  );
};
